/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  PaymentTransactionsAPIResponse,
  PaymentTransactionsInitialProperties,
  RequestPaymentTransactionsInterface,
} from '../../interface/payment-transaction-interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import paymentTransactionsApi from '../../utils/api/v2/payment-gateway';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Map payment method string to valid transaction type
 * @param {string} method - payment method string
 * @returns {string | null} mapped transaction type or null
 */
const mapPaymentMethodToTransactionType = (method: string): string | null => {
  const lower = method.toLowerCase();
  if (lower === 'preauth') return 'preAuth';
  if (lower === 'debit') return 'debit';
  if (lower === 'credit') return 'credit';
  if (lower === 'refund') return 'refund';
  if (lower === 'payout') return 'payout';

  if (lower.includes('credit')) return 'credit';
  if (lower.includes('debit')) return 'debit';
  if (lower.includes('mada')) return 'debit';
  if (lower.includes('apple')) return 'debit';
  if (lower.includes('paypal')) return 'debit';
  if (lower.includes('preauth')) return 'preAuth';
  if (lower.includes('payout')) return 'payout';
  return null;
};

export const fetchPaymentTransactions = createAsyncThunk<
  PaymentTransactionsAPIResponse,
  RequestPaymentTransactionsInterface
>(
  'payment-transactions/fetchPaymentTransactions',
  async (data: RequestPaymentTransactionsInterface, { dispatch }) => {
    try {
      const queryParameters: any = {};
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'type' && value) {
          const allowedTypes = ['preAuth', 'debit', 'credit', 'refund', 'payout'];
          let stringValue = '';
          if (Array.isArray(value)) {
            stringValue = value.join(',');
          } else if (typeof value === 'string') {
            stringValue = value;
          }

          const mapped = stringValue
            .split(',')
            .map((value_) => mapPaymentMethodToTransactionType(value_.trim()))
            .filter((value_): value_ is string => value_ !== null && allowedTypes.includes(value_));

          if (mapped.length > 0) {
            queryParameters[key] = mapped.join(',');
          }
        } else if (Array.isArray(value)) {
          queryParameters[key] = value.join(',');
        } else {
          queryParameters[key] = value;
        }
      });

      const response = await paymentTransactionsApi.fetchPaymentTransactionsList(
        removeEmptyKeys(queryParameters),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch payment transactions',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchPaymentTransactionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'payment-transactions/fetchPaymentTransactionFilters',
  async () => {
    return [];
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search Driver ID...',
    key: 'userCustomId',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Status',
    key: 'status',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Pending', value: 'pending' },
      { label: 'Success', value: 'success' },
      { label: 'Failed', value: 'failed' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Type',
    key: 'type',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Pre-auth', value: 'preAuth' },
      { label: 'Debit', value: 'debit' },
      { label: 'Credit', value: 'credit' },
      { label: 'Refund', value: 'refund' },
      { label: 'Payout', value: 'payout' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Gateway',
    key: 'gateway',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'HyperPay', value: 'hyperpay' },
      { label: 'Stripe', value: 'stripe' },
      { label: 'Checkout.com', value: 'checkoutcom' },
      { label: 'Urway', value: 'urway' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    key1: 'startDate',
    value1: '',
    key2: 'endDate',
    value2: '',
    value: '',
    type: filterType.DATE_RANGE,
    applyFilter: false,
    enable: true,
  },
];

export const refreshTranstion = createAsyncThunk<
  PaymentTransactionsAPIResponse,
  RequestPaymentTransactionsInterface
>(
  'payment-transactions/refreshTranstion',
  async (data: RequestPaymentTransactionsInterface, { dispatch }) => {
    try {
      const response = await paymentTransactionsApi.fetchPaymentTransactionsList(
        removeEmptyKeys({ ...data }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const initialState: PaymentTransactionsInitialProperties = {
  records: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const paymentTransactionSlice = createSlice({
  name: 'payment-transactions',
  initialState,
  reducers: {
    /**
     * Update payment transaction filters
     */
    updatePaymentTransactionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Reset filter applied status
     */
    disablePaymentTransactionFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort data
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Clear all state
     */
    clearPaymentTransactionData: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * Set gateway filter options dynamically based on configured gateways
     */
    setGatewayFilterOptions: (state, action: PayloadAction<{ label: string; value: string }[]>) => {
      const gatewayFilter = state.filters.find((f) => f.key === 'gateway');
      if (gatewayFilter) {
        gatewayFilter.listOfValue = action.payload;
      }
    },
  },
  /**
   * extraReducers for async actions
   * @param builder builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<PaymentTransactionsInitialProperties>) => {
    builder
      .addCase(fetchPaymentTransactions.pending, (state: PaymentTransactionsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchPaymentTransactions.fulfilled,
        (
          state: PaymentTransactionsInitialProperties,
          action: PayloadAction<PaymentTransactionsAPIResponse>,
        ) => {
          state.loading = false;
          state.records = action.payload.records.map((record) => ({
            ...record,
            id: record.transactionId,
          }));
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.total;
        },
      )
      .addCase(fetchPaymentTransactions.rejected, (state: PaymentTransactionsInitialProperties) => {
        state.loading = false;
      });
    builder.addCase(
      refreshTranstion.fulfilled,
      (
        state: PaymentTransactionsInitialProperties,
        action: PayloadAction<PaymentTransactionsAPIResponse>,
      ) => {
        state.records = action.payload.records.map((record) => ({
          ...record,
          id: record.transactionId,
        }));
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.total;
      },
    );
    builder
      .addCase(fetchPaymentTransactionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchPaymentTransactionFilters.fulfilled, (state) => {
        state.filterListLoading = false;
      })
      .addCase(fetchPaymentTransactionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updatePaymentTransactionFilters,
  disablePaymentTransactionFilterApplied,
  setSortData,
  clearPaymentTransactionData,
  setGatewayFilterOptions,
} = paymentTransactionSlice.actions;

export default paymentTransactionSlice.reducer;
